import { useState, useEffect } from 'react';
import { pdfjs, Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useApiContext } from '@/contexts/apiContext';
import ErrorMessage from '../errorMessage';
import { useElementSize } from '@custom-react-hooks/use-element-size';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

type Props = {
  pdfDataUrl: string;
  maxHeight?: number;
  showLabel?: boolean;
  label?: string;
  error?: string;
  releventPages?: number[];
};

const PdfViewer = ({ pdfDataUrl, showLabel = true, label, error, releventPages = [], maxHeight }: Props) => {
  const apiContext = useApiContext();
  const [headerRef, headerSize] = useElementSize();

  const [numberOfPages, setNumberOfPages] = useState<number>();
  const [pagesType, setPagesType] = useState('all');
  const [pages, setPages] = useState([1]);
  const [showPagesTypeOption, setShowPagesTypeOption] = useState(false);

  const onDocumentLoadSuccess = ({ numPages: nextNumPages }: PDFDocumentProxy) => {
    setNumberOfPages(nextNumPages);
    if (releventPages && releventPages.length > 0) {
      setPages(
        releventPages
          .filter((p) => p > 0 && p <= nextNumPages)
          .sort(function (a, b) {
            return a - b;
          }),
      );
      setShowPagesTypeOption(true);
      setPagesType('relevent');
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        border: label || error ? 1 : 0,
        borderColor: 'divider',
        width: '100%',
        maxWidth: '100%',
        ...(maxHeight && maxHeight > 0 ? { height: maxHeight, maxHeight } : {}),
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {showLabel && (label || error) && (
        <CardHeader
          title={
            <Stack direction="row" spacing={2} justifyContent="space-between" alignItems={'center'}>
              <Typography sx={{ textOverflow: 'ellipsis' }}>{label}</Typography>
              {showPagesTypeOption && (
                <RadioGroup
                  value={pagesType}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setPagesType((event.target as HTMLInputElement).value);
                  }}
                  row
                >
                  <FormControlLabel
                    value={'all'}
                    control={<Radio size="small" />}
                    label={'Show all pages'}
                  ></FormControlLabel>
                  <FormControlLabel
                    value={'relevent'}
                    control={<Radio size="small" />}
                    label={'Show relevent pages'}
                  ></FormControlLabel>
                </RadioGroup>
              )}
            </Stack>
          }
          subheader={error && <ErrorMessage error={error} />}
          sx={{ p: 1 }}
          ref={headerRef}
          disableTypography
        />
      )}
      {showLabel && (label || error) && <Divider />}
      <CardContent sx={{ overflowY: 'auto', flex: 1, minHeight: 0, p: 0, display: 'flex', justifyContent: 'center' }}>
        {pdfDataUrl && (
          <Document
            key={pdfDataUrl}
            loading={<Typography>Loading...</Typography>}
            file={pdfDataUrl}
            onLoadSuccess={onDocumentLoadSuccess}
          >
            {numberOfPages && pagesType === 'all'
              ? Array.from(new Array(numberOfPages), (el, index) => (
                  <Box key={index} border={1} borderColor={'divider'} marginBottom={1}>
                    <Page key={`page_${index + 1}`} pageNumber={index + 1} />
                  </Box>
                ))
              : pagesType === 'relevent'
                ? pages.map((p, index) => (
                    <Box key={index} border={1} borderColor={'divider'} marginBottom={1}>
                      <Page key={`page_${p + 1}`} pageNumber={p} />
                    </Box>
                  ))
                : null}
          </Document>
        )}
      </CardContent>
    </Card>
  );
};

export default PdfViewer;
